import { zodResolver } from "@hookform/resolvers/zod";
import type { ItemResponse, ItemResponseFormField } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import DateTimePickerModal from "@memorang/ui/components/DateTimePickerModal";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { useEffect, useMemo, useState } from "react";
import {
	type Control,
	Controller,
	type FieldErrors,
	useForm,
} from "react-hook-form";
import { Pressable } from "react-native";
import { Text, TextInput } from "react-native-paper";
import { DatePickerModal, TimePickerModal } from "react-native-paper-dates";
import { createFormSchema } from "./helpers";

type FormData = Record<string, string>;

const countryCodeToCurrencyIcon: Record<string, string> = {
	IN: "currency-inr",
	US: "currency-usd",
	UK: "currency-gbp",
	CA: "currency-cad",
	AU: "currency-aud",
};

const formatDateToString = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

interface Props {
	formConfig: Extract<ItemResponse, { type: "form" }>;
	onWatch?: (data: FormData) => void;
	countryCode?: string;
	disabled?: boolean;
	userResponse?: Record<string, string>;
}

const FormInput = ({
	field,
	control,
	errors,
	countryCode = "IN",
	disabled = false,
}: {
	field: ItemResponseFormField;
	control: Control<FormData>;
	errors: FieldErrors<FormData>;
	countryCode?: string;
	disabled?: boolean;
}) => {
	const theme = useAppTheme();
	const [datePickerVisible, setDatePickerVisible] = useState(false);
	const [timePickerVisible, setTimePickerVisible] = useState(false);
	const [dateTimePickerVisible, setDateTimePickerVisible] = useState(false);

	const getEndAdornmentIcon = (onPress?: () => void) => {
		const getFunctionalIcon = () => {
			switch (field.inputType) {
				case "date":
					return <TextInput.Icon icon="calendar-outline" onPress={onPress} />;
				case "time":
					return <TextInput.Icon icon="clock-outline" onPress={onPress} />;
				case "datetime":
					return <TextInput.Icon icon="calendar-clock" onPress={onPress} />;
				default:
					return null;
			}
		};

		return getFunctionalIcon();
	};

	const renderInputByType = (
		onChange: (value: string) => void,
		value: string,
		onBlur?: () => void,
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	) => {
		switch (field.inputType) {
			case "date":
				return (
					<>
						<Pressable
							onPress={() => setDatePickerVisible(true)}
							disabled={disabled}
						>
							<TextInput
								label={field.required ? `${field.label} *` : field.label}
								placeholder={field.placeholder}
								value={value || ""}
								right={getEndAdornmentIcon(() => setDatePickerVisible(true))}
								editable={false}
								mode="outlined"
								pointerEvents="none"
								error={!!errors[field.id]}
								disabled={disabled}
							/>
						</Pressable>
						{datePickerVisible && (
							<DatePickerModal
								visible={datePickerVisible}
								saveLabel="Set date"
								onDismiss={() => setDatePickerVisible(false)}
								onConfirm={(params) => {
									if ("date" in params && params.date) {
										onChange(formatDateToString(params.date));
									}
									setDatePickerVisible(false);
								}}
								date={value ? new Date(value) : undefined}
								mode="single"
								label="Select date"
								locale="en"
							/>
						)}
					</>
				);

			case "time":
				return (
					<>
						<Pressable
							onPress={() => setTimePickerVisible(true)}
							disabled={disabled}
						>
							<TextInput
								label={field.required ? `${field.label} *` : field.label}
								placeholder={field.placeholder}
								value={value || ""}
								right={getEndAdornmentIcon(() => setTimePickerVisible(true))}
								editable={false}
								mode="outlined"
								pointerEvents="none"
								error={!!errors[field.id]}
								disabled={disabled}
							/>
						</Pressable>
						<TimePickerModal
							visible={timePickerVisible}
							cancelLabel="Cancel"
							confirmLabel="Set time"
							onDismiss={() => setTimePickerVisible(false)}
							onConfirm={({
								hours,
								minutes,
							}: { hours: number; minutes: number }) => {
								const formattedHours = hours.toString().padStart(2, "0");
								const formattedMinutes = minutes.toString().padStart(2, "0");
								onChange(`${formattedHours}:${formattedMinutes}`);
								setTimePickerVisible(false);
							}}
							use24HourClock={false}
							hours={value ? Number.parseInt(value.split(":")[0]) || 14 : 14}
							minutes={value ? Number.parseInt(value.split(":")[1]) || 30 : 30}
						/>
					</>
				);

			case "datetime":
				return (
					<>
						<Pressable onPress={() => setDateTimePickerVisible(true)}>
							<TextInput
								label={field.required ? `${field.label} *` : field.label}
								placeholder={field.placeholder}
								value={value || ""}
								right={getEndAdornmentIcon(() =>
									setDateTimePickerVisible(true),
								)}
								editable={false}
								mode="outlined"
								pointerEvents="none"
								error={!!errors[field.id]}
								disabled={disabled}
							/>
						</Pressable>
						<DateTimePickerModal
							isVisible={dateTimePickerVisible}
							mode="datetime"
							onConfirm={(selectedDateTime: Date) => {
								const dateString = formatDateToString(selectedDateTime);
								const timeString = selectedDateTime.toLocaleTimeString(
									"en-US",
									{
										hour12: false,
										hour: "2-digit",
										minute: "2-digit",
									},
								);
								onChange(`${dateString} ${timeString}`);
								setDateTimePickerVisible(false);
							}}
							onCancel={() => setDateTimePickerVisible(false)}
							date={value ? new Date(value) : undefined}
						/>
					</>
				);

			case "number":
				return (
					<TextInput
						label={field.required ? `${field.label} *` : field.label}
						placeholder={field.placeholder}
						value={value || ""}
						onChangeText={(text) => {
							const numericText = text.replace(/[^0-9]/g, "");
							onChange(numericText);
						}}
						onBlur={onBlur}
						keyboardType="numeric"
						mode="outlined"
						right={getEndAdornmentIcon()}
						error={!!errors[field.id]}
						disabled={disabled}
					/>
				);

			case "currency":
				return (
					<TextInput
						label={field.required ? `${field.label} *` : field.label}
						placeholder={field.placeholder}
						value={value}
						onChangeText={(text) => {
							const numericText = text.replace(/[^0-9.]/g, "");
							onChange(numericText);
						}}
						onBlur={onBlur}
						keyboardType="numeric"
						left={
							<TextInput.Icon
								icon={countryCodeToCurrencyIcon[countryCode.toUpperCase()]}
							/>
						}
						right={getEndAdornmentIcon()}
						mode="outlined"
						error={!!errors[field.id]}
						disabled={disabled}
					/>
				);

			case "email":
				return (
					<TextInput
						label={field.required ? `${field.label} *` : field.label}
						placeholder={field.placeholder}
						value={value || ""}
						onChangeText={onChange}
						onBlur={onBlur}
						keyboardType="email-address"
						autoCapitalize="none"
						autoComplete="email"
						left={<TextInput.Icon icon="email-outline" />}
						right={getEndAdornmentIcon()}
						mode="outlined"
						error={!!errors[field.id]}
						disabled={disabled}
					/>
				);

			default:
				return (
					<TextInput
						label={field.required ? `${field.label} *` : field.label}
						placeholder={field.placeholder}
						value={value || ""}
						onChangeText={onChange}
						right={getEndAdornmentIcon()}
						mode="outlined"
						error={!!errors[field.id]}
						disabled={disabled}
					/>
				);
		}
	};

	return (
		<Box style={{ gap: 4 }}>
			<Controller
				name={field.id}
				control={control}
				render={({ field: { onChange, value, onBlur } }) => (
					<>{renderInputByType(onChange, value, onBlur)}</>
				)}
			/>
			<Text variant="bodySmall" style={{ paddingLeft: 12 }}>
				{field.helperText}
			</Text>
			{errors[field.id] && (
				<Text
					variant="bodySmall"
					style={{ paddingLeft: 12, color: theme.colors.error }}
				>
					{errors[field.id]?.message}
				</Text>
			)}
		</Box>
	);
};

const ItemResponseForm = ({
	formConfig,
	onWatch,
	countryCode = "IN",
	disabled = false,
	userResponse,
}: Props) => {
	const formSchema = useMemo(
		() => createFormSchema(formConfig.fields),
		[formConfig.fields],
	);

	const {
		control,
		subscribe,
		formState: { errors },
	} = useForm<FormData>({
		resolver: zodResolver(formSchema),
		mode: "onBlur",
		defaultValues: formConfig.fields.reduce(
			(acc: FormData, field: ItemResponseFormField) => {
				acc[field.id] = userResponse?.[field.id] ?? "";
				return acc;
			},
			{} as FormData,
		),
	});

	useEffect(() => {
		if (!onWatch) {
			return;
		}

		const callback = subscribe({
			formState: {
				values: true,
			},
			callback: (data) => {
				onWatch(data.values);
			},
		});

		return () => callback();
	}, [onWatch, subscribe]);

	return (
		<Box gap={16}>
			{formConfig.fields.map((field: ItemResponseFormField) => (
				<FormInput
					key={field.id}
					field={field}
					control={control}
					errors={errors}
					countryCode={countryCode}
					disabled={disabled}
				/>
			))}
		</Box>
	);
};

export default ItemResponseForm;
